import { ReactElement, useRef } from 'react';
import readXlsxFile from 'read-excel-file';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
  DEFAULT_OPC_N3_BOUNDARIES,
  OPC_BIN_COUNT,
  BOUNDARIES_STORAGE_KEY,
  fmtBoundary
} from './particleBinShared';

/**
 * Everything instrument-specific about a set of size boundaries, so the same
 * upload control serves the OPC-N3's 24 bins and the APS's 52 channels.
 */
export type BoundaryConfig = {
  /** Number of bins; there is always one more edge than bins. */
  binCount: number;
  /** Built-in edges used until a spreadsheet is uploaded. */
  defaults: number[];
  /** LocalStorage key the uploaded edges persist under. */
  storageKey: string;
  /** Instrument name shown when the defaults are in use, e.g. "OPC-N3". */
  instrument: string;
  /** What the bins are called in the template and messages. */
  binWord: string;
};

/** The OPC-N3's 24 Alphasense bins. */
export const OPC_BOUNDARY_CONFIG: BoundaryConfig = {
  binCount: OPC_BIN_COUNT,
  defaults: DEFAULT_OPC_N3_BOUNDARIES,
  storageKey: BOUNDARIES_STORAGE_KEY,
  instrument: 'OPC-N3',
  binWord: 'bin'
};

export type BoundariesState = {
  /** Ascending size edges in µm, one more than the bin count. */
  edges: number[];
  /** Null = built-in instrument defaults; otherwise the uploaded file name. */
  source: string | null;
};

/**
 * Load persisted boundaries (or the defaults) — used by the page on init.
 * @param config - Instrument boundary configuration.
 * @returns Stored boundaries state.
 */
export function loadStoredBoundaries(config: BoundaryConfig = OPC_BOUNDARY_CONFIG): BoundariesState {
  try {
    const raw = localStorage.getItem(config.storageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed.edges) && parsed.edges.length === config.binCount + 1) {
        return { edges: parsed.edges, source: parsed.source ?? 'uploaded file' };
      }
    }
  } catch {
    // Fall through to defaults on any storage/parse issue.
  }
  return { edges: config.defaults, source: null };
}

/**
 * Extract the ascending size edges from arbitrary spreadsheet rows.
 * Accepted layouts (extra text/header cells are ignored): 24 rows of
 * lower+upper µm columns (a leading bin-number column is fine); 25 rows whose
 * last numeric cell is the edge; or any layout whose numeric cells flatten to
 * exactly 25 values in order.
 * @param rows - Parsed sheet rows.
 * @param config - Instrument boundary configuration.
 * @returns The edges, one more than the bin count.
 * @throws Error with a user-facing message when no layout matches.
 */
export function parseBoundaryRows(
  rows: unknown[][],
  config: BoundaryConfig = OPC_BOUNDARY_CONFIG
): number[] {
  const { binCount } = config;
  const numericRows = rows
    .map((row) => row.filter((c): c is number => typeof c === 'number' && Number.isFinite(c)))
    .filter((row) => row.length > 0);

  let edges: number[] | null = null;
  if (numericRows.length === binCount && numericRows.every((r) => r.length >= 2)) {
    // lower/upper per bin: lower = second-to-last numeric (tolerates a bin-# column).
    const lowers = numericRows.map((r) => r[r.length - 2]);
    edges = [...lowers, numericRows[numericRows.length - 1][numericRows[numericRows.length - 1].length - 1]];
  } else if (numericRows.length === binCount + 1) {
    edges = numericRows.map((r) => r[r.length - 1]);
  } else {
    const flat = numericRows.flat();
    if (flat.length === binCount + 1) {
      edges = flat;
    }
  }

  if (!edges) {
    throw new Error(
      `Could not find ${binCount + 1} ${config.binWord} edges. Upload either ${binCount + 1} boundary values or ${binCount} lower/upper rows (see template).`
    );
  }
  for (let i = 0; i < edges.length; i += 1) {
    if (edges[i] <= 0 || (i > 0 && edges[i] <= edges[i - 1])) {
      throw new Error('Boundaries must be positive and strictly ascending.');
    }
  }
  return edges;
}

type BinBoundariesControlProps = {
  boundaries: BoundariesState;
  onChange: (next: BoundariesState) => void;
  /** Which instrument's boundaries these are. Defaults to the OPC-N3. */
  config?: BoundaryConfig;
};

/**
 * Upload / reset control for the particle-size bin boundaries used to label
 * every chart. Boundaries persist in localStorage until reset.
 * @param props - Current state + change callback.
 * @returns Control row.
 */
export default function BinBoundariesControl(props: BinBoundariesControlProps): ReactElement {
  const { boundaries, onChange, config = OPC_BOUNDARY_CONFIG } = props;
  const fileRef = useRef<HTMLInputElement>(null);

  /**
   * Parse the chosen workbook and persist the boundaries.
   * @param file - Uploaded .xlsx file.
   */
  const handleFile = async (file: File): Promise<void> => {
    try {
      const rows = await readXlsxFile(file);
      const edges = parseBoundaryRows(rows as unknown[][], config);
      const next = { edges, source: file.name };
      localStorage.setItem(config.storageKey, JSON.stringify(next));
      onChange(next);
      toast.success(`Boundaries loaded from ${file.name}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not read the boundaries file.');
    }
  };

  /** Restore the built-in OPC-N3 boundaries. */
  const reset = (): void => {
    localStorage.removeItem(config.storageKey);
    onChange({ edges: config.defaults, source: null });
  };

  /** Generate and download an xlsx template pre-filled with the defaults. */
  const downloadTemplate = (): void => {
    const heading = config.binWord.charAt(0).toUpperCase() + config.binWord.slice(1);
    const rows: (string | number)[][] = [[heading, 'Lower (µm)', 'Upper (µm)']];
    for (let i = 0; i < config.binCount; i += 1) {
      rows.push([i, config.defaults[i], config.defaults[i + 1]]);
    }
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Boundaries');
    XLSX.writeFile(book, `${config.instrument.toLowerCase()}-boundaries-template.xlsx`);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-base text-gray-400">
        {config.binWord.charAt(0).toUpperCase() + config.binWord.slice(1)} boundaries:{' '}
        <span className="text-gray-200">
          {boundaries.source ?? `${config.instrument} defaults`} ({fmtBoundary(boundaries.edges[0])}–
          {fmtBoundary(boundaries.edges[boundaries.edges.length - 1])} µm)
        </span>
      </span>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="flex items-center gap-1 rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-base text-gray-300 transition hover:text-gray-100"
      >
        <span className="material-icons-outlined text-lg">upload_file</span>
        Upload Excel
      </button>
      <button
        type="button"
        onClick={downloadTemplate}
        className="rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-base text-gray-400 transition hover:text-gray-200"
      >
        Template
      </button>
      {boundaries.source && (
        <button
          type="button"
          onClick={reset}
          className="rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-base text-gray-400 transition hover:text-gray-200"
        >
          Reset defaults
        </button>
      )}
    </div>
  );
}
